<script setup lang="ts">
import type { TaskUser } from '~/models/AssignTask'
import { useI18n } from 'vue-i18n'
import { computed, ref } from 'vue'
import { faMagnifyingGlass, faXmark } from '@fortawesome/duotone-regular-svg-icons'
import { PendoFeature } from '~/models/Pendo'
import { TaskStatus } from '~/models/AssignTask'
import ScrollBox from '~/components/utils/ScrollBox.vue'
import Button from '~/components/ui/Button.vue'
import AuCheckbox from '~/components/ui/AuCheckbox.vue'
import SearchStudentsDialog from '~/components/products/exams/assignment/SearchStudentsDialog.vue'

const { taskId } = defineProps<{ taskId: string | null }>()
const students = defineModel<TaskUser[]>({ default: [] })

const { t } = useI18n()

const searchQuery = ref('')
const isScrolled = ref(false)
const isLoadingList = ref(false)

const filteredStudents = computed(() => students.value
  .filter(student => (student.fullName ?? '').toLowerCase().includes(searchQuery.value.toLowerCase()))
  .sort((a, b) => (a.fullName ?? '').localeCompare(b.fullName ?? '')))

const handleScroll = (e: Event) => {
  const target = e.target as HTMLElement
  isScrolled.value = target.scrollTop > 0
}

const toggleStudentSelection = (student: TaskUser) => {
  students.value = students.value.map(s =>
    s.userId === student.userId
      ? { ...s, status: s.status === TaskStatus.Open ? TaskStatus.Closed : TaskStatus.Open }
      : s
  )
}

const toggleSelectAll = () => {
  const allSelected = students.value.every(student => student.status === TaskStatus.Open)
  students.value = allSelected
    ? students.value.map(student => ({ ...student, status: TaskStatus.Closed }))
    : students.value.map(student => ({ ...student, status: TaskStatus.Open }))
}

const addStudents = (studentsToAdd: TaskUser[]) => {
  students.value = [...studentsToAdd, ...students.value]
}
</script>

<template>
  <div>
    <span
      class="font-medium"
      v-text="t('assign.chooseStudents')"
    />
    <form
      v-if="students.length"
      class="bg-secondary mt-2 rounded-md pt-4"
    >
      <div
        class="flex w-full items-center justify-between px-4 pb-4 transition-all"
        :class="{ 'shadow-md': isScrolled }"
      >
        <div class="relative w-2/3">
          <div class="text-gray-40 absolute top-1/2 left-5 z-10 flex -translate-y-1/2 justify-center text-base">
            <font-awesome-icon
              :icon="faMagnifyingGlass"
              :fade="isLoadingList"
            />
          </div>
          <input
            v-model="searchQuery"
            type="text"
            name="search"
            :placeholder="t('assign.searchPlaceholder')"
            class="placeholder:text-gray-40 border-gray-10 w-full rounded-xl border bg-white p-2 pl-12 text-base transition focus-visible:ring"
          >
          <Button
            v-if="searchQuery.length"
            shape="square"
            size="sm"
            variant="ghost"
            class="absolute top-1/2 right-3 z-10 -translate-y-1/2 text-xl"
            @click="searchQuery = ''"
          >
            <font-awesome-icon :icon="faXmark" />
          </Button>
        </div>
        <div>
          <label
            class="flex cursor-pointer items-center gap-2 rounded-xl p-2 text-sm font-medium transition hover:bg-white/50"
          >
            <span v-text="t('assign.chooseAll')" />
            <AuCheckbox
              :model-value="students.every(student => student.status === TaskStatus.Open)"
              :data-pendo="PendoFeature.TermExam.SelectStudent"
              @update:model-value="toggleSelectAll"
            />
          </label>
        </div>
      </div>
      <ScrollBox
        class="max-h-96 !overflow-x-hidden px-4 pb-4"
        @scroll="handleScroll"
      >
        <transition-group
          v-if="filteredStudents.length"
          name="list"
          tag="ul"
          class="relative"
        >
          <li
            v-for="student in filteredStudents"
            :key="`student-${student.userId}`"
            class="border-green-10 size-full border-b last:border-b-0"
          >
            <label
              :for="`id-${taskId}-${student.userId}`"
              class="hover:bg-secondary-hover flex size-full cursor-pointer items-center justify-between px-1 py-3 font-medium transition-all hover:rounded hover:pl-2"
            >
              <span v-text="student.fullName" />
              <AuCheckbox
                :id="`id-${taskId}-${student.userId}`"
                :model-value="student.status === TaskStatus.Open"
                :data-pendo="PendoFeature.TermExam.SelectStudent"
                @update:model-value="toggleStudentSelection(student)"
              />
            </label>
          </li>
        </transition-group>
        <p
          v-else
          class="text-sm"
          v-text="t('assign.noStudents')"
        />
      </ScrollBox>
    </form>
    <p
      v-else
      class="text-gray-40 mt-1"
      v-text="t('assign.noStudentsInGroup')"
    />
    <SearchStudentsDialog
      :current-students="students"
      @add-students="addStudents"
    />
  </div>
</template>

<style scoped>
.list-move,
.list-enter-active {
  transition: all 0.5s cubic-bezier(0.55, 0, 0.1, 1);
}

.list-leave-active {
  transition: all 0.2s cubic-bezier(0.55, 0, 0.1, 1);
}

.list-enter-from,
.list-leave-to {
  opacity: 0;
  transform: scaleY(0.01) translate(30px, 0);
}

.list-leave-active {
  position: absolute;
}
</style>
